import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { UploadDocumentsProps } from "@/components/UploadDocuments";
import { useTRPC } from "@/trpc";
import { cn } from "@/lib/utils";
import type { CredentialCategory, VaultFile } from "../../types";
import type { useVaultFileDownloads } from "../../hooks/use-vault-file-downloads";
import {
  filterVaultFiles,
  hasVaultFileFilters,
  INITIAL_VAULT_FILE_FILTERS,
  VAULT_FILES_PAGE_SIZE,
  vaultFileSize,
  type VaultFileFilters,
} from "../../lib/vault-file-filters";
import {
  vaultFilesQueryOptions,
  type CredentialVaultScope,
} from "../../lib/credential-vault-scope";
import { CredentialVaultUpload } from "../credential-vault-upload";
import { VaultFilesSort, VaultFilesToolbar } from "./vault-files-toolbar";
import { VaultFileRow } from "./vault-file-row";
import { VaultDownloadFeedback } from "./vault-download-feedback";

const VAULT_FILES_POLL_INTERVAL_MS = 3000;

export function VaultFilesView({
  scope,
  canUpload = true,
  categories,
  downloads,
  uploadDisabled,
  reviewContent,
  reviewDocumentId,
  onUploadStateChange,
  onPreview,
}: {
  scope: CredentialVaultScope;
  canUpload?: boolean;
  categories: CredentialCategory[];
  downloads: ReturnType<typeof useVaultFileDownloads>;
  uploadDisabled: boolean;
  reviewContent?: ReactNode;
  reviewDocumentId?: string | null;
  onUploadStateChange: NonNullable<UploadDocumentsProps["onUploadStateChange"]>;
  onPreview: (file: VaultFile) => void;
}) {
  const trpc = useTRPC();
  const reviewRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (reviewDocumentId) {
      reviewRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [reviewDocumentId]);
  const query = useQuery({
    ...vaultFilesQueryOptions(trpc, scope),
    refetchInterval: (current) =>
      (current.state.data?.files ?? []).some(
        (file) =>
          file.reviewState === "reading" ||
          (!file.canDownload && file.scanStatus !== "quarantined"),
      )
        ? VAULT_FILES_POLL_INTERVAL_MS
        : false,
  });
  const [filters, setFilters] = useState(INITIAL_VAULT_FILE_FILTERS);
  const [selection, setSelection] = useState<ReadonlyMap<string, VaultFile>>(
    () => new Map(),
  );
  const [selectionMode, setSelectionMode] = useState(false);
  const [page, setPage] = useState(1);
  const files = query.data?.files ?? [];
  const matching = filterVaultFiles({ files, filters });
  const downloadable = matching.filter((file) => file.canDownload);
  const selected = downloadable.filter((file) => selection.has(file.id));
  const availableIds = new Set(downloadable.map((file) => file.id));
  const unavailableSelected = [...selection.values()].filter(
    (file) => !availableIds.has(file.id),
  );
  const hasSelection = selection.size > 0;
  const pageCount = Math.max(
    1,
    Math.ceil(matching.length / VAULT_FILES_PAGE_SIZE),
  );
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * VAULT_FILES_PAGE_SIZE;
  const visibleFiles = matching.slice(
    pageStart,
    pageStart + VAULT_FILES_PAGE_SIZE,
  );
  const filtered = hasVaultFileFilters(filters);
  const exportFiles = hasSelection ? selected : downloadable;
  const exceedsLimit =
    exportFiles.length > downloads.limits.maxFiles ||
    exportFiles.reduce((total, file) => total + file.fileSizeBytes, 0) >
      downloads.limits.maxTotalBytes;

  function changeFilters(patch: Partial<VaultFileFilters>) {
    setFilters((current) => ({ ...current, ...patch }));
    if (
      patch.search !== undefined ||
      patch.category !== undefined ||
      patch.status !== undefined
    ) {
      setSelection(new Map());
    }
    setPage(1);
  }

  function clearFilters() {
    setFilters(INITIAL_VAULT_FILE_FILTERS);
    setSelection(new Map());
    setPage(1);
  }

  function selectFile({
    file,
    checked,
  }: {
    file: VaultFile;
    checked: boolean;
  }) {
    if (!file.canDownload || downloads.isBusy) {
      return;
    }
    setSelection((current) => {
      const next = new Map(current);
      if (checked) {
        next.set(file.id, file);
      } else {
        next.delete(file.id);
      }
      return next;
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5">
        {canUpload && (
          <CredentialVaultUpload
            scope={scope}
            disabled={uploadDisabled || downloads.isBusy}
            onUploadStateChange={onUploadStateChange}
          />
        )}
        {reviewContent && <div ref={reviewRef}>{reviewContent}</div>}
        <VaultFilesToolbar
          filters={filters}
          categories={categories}
          onChange={changeFilters}
          onClear={clearFilters}
        />
        {query.isError && (
          <div
            role="alert"
            className="space-y-2 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700"
          >
            <p>
              Could not load files. Your search and selection are still here.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              Retry loading files
            </Button>
          </div>
        )}
        {query.isPending && (
          <p role="status" className="text-sm text-slate-600">
            Loading files…
          </p>
        )}
        {query.data && (
          <>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p
                  aria-live="polite"
                  className="text-sm font-semibold text-slate-900"
                >
                  {matching.length} {matching.length === 1 ? "file" : "files"}
                  {filtered && ` of ${files.length}`}
                  <span className="mt-0.5 block text-xs font-normal text-slate-500">
                    {downloadable.length} available to download
                  </span>
                </p>
                <VaultFilesSort
                  value={filters.sort}
                  onChange={(sort) => changeFilters({ sort })}
                />
              </div>
              {matching.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="sm:hidden"
                    disabled={downloads.isBusy}
                    onClick={() => {
                      setSelectionMode((current) => !current);
                      setSelection(new Map());
                    }}
                  >
                    {selectionMode ? "Done selecting" : "Select"}
                  </Button>
                  <div
                    className={cn(
                      "flex-wrap items-center gap-2",
                      selectionMode ? "flex" : "hidden sm:flex",
                    )}
                  >
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto px-0 text-xs"
                      disabled={downloads.isBusy || downloadable.length === 0}
                      onClick={() =>
                        setSelection(
                          new Map(downloadable.map((file) => [file.id, file])),
                        )
                      }
                    >
                      Select all matching files ({downloadable.length})
                    </Button>
                    {hasSelection && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        disabled={downloads.isBusy}
                        onClick={() => setSelection(new Map())}
                      >
                        Clear selection
                      </Button>
                    )}
                  </div>
                  {!hasSelection && downloadable.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-auto gap-2 rounded-lg border-slate-300 text-xs"
                      disabled={downloads.isBusy || exceedsLimit}
                      onClick={() => void downloads.downloadFiles(downloadable)}
                    >
                      <Download className="size-4" aria-hidden="true" />
                      {`${filtered ? "Download results" : "Download all"} (${downloadable.length})`}
                    </Button>
                  )}
                </div>
              )}
              {!hasSelection && exceedsLimit && (
                <p
                  role="status"
                  className="text-xs leading-relaxed text-amber-800"
                >
                  Select fewer files. ZIP downloads include up to{" "}
                  {downloads.limits.maxFiles} files, totaling{" "}
                  {vaultFileSize(downloads.limits.maxTotalBytes)}.
                </p>
              )}
            </div>
            {files.length === 0 && (
              <div className="space-y-2 rounded-2xl border border-slate-200 bg-white px-5 py-8 text-center">
                <FileText
                  className="mx-auto size-8 text-slate-400"
                  aria-hidden="true"
                />
                <h3 className="font-semibold text-slate-900">No files yet</h3>
                <p className="text-sm text-slate-600">
                  {canUpload
                    ? "Use Upload a file above to update a credential or add supporting evidence."
                    : "Files attached to this business unit’s record will appear here."}
                </p>
              </div>
            )}
            {files.length > 0 && matching.length === 0 && (
              <div className="space-y-2 rounded-2xl border border-slate-200 bg-white px-5 py-8 text-center">
                <h3 className="font-semibold text-slate-900">
                  No matching files
                </h3>
                <p className="text-sm text-slate-600">
                  Try a different search or clear your filters.
                </p>
              </div>
            )}
            {visibleFiles.length > 0 && (
              <ul
                aria-label="Credential files"
                className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white"
              >
                {visibleFiles.map((file) => (
                  <VaultFileRow
                    key={file.id}
                    file={file}
                    selected={selection.has(file.id) && file.canDownload}
                    selectionMode={selectionMode}
                    downloadsBusy={downloads.isBusy}
                    onSelect={(checked) => selectFile({ file, checked })}
                    onPreview={() => onPreview(file)}
                  />
                ))}
              </ul>
            )}
            {pageCount > 1 && (
              <nav
                aria-label="File pages"
                className="flex items-center justify-between gap-2"
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <p className="text-xs text-slate-600">
                  Page {currentPage} of {pageCount}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={currentPage === pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Button>
              </nav>
            )}
          </>
        )}
      </div>
      {(hasSelection ||
        downloads.isBusy ||
        downloads.failures.length > 0 ||
        downloads.successfulCount > 0) && (
        <div className="shrink-0 space-y-3 border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
          <VaultDownloadFeedback downloads={downloads} />
          {unavailableSelected.length > 0 && (
            <div
              role="status"
              className="space-y-1 text-xs leading-relaxed text-amber-800"
            >
              <p>
                These selected files are no longer available in the current
                results and will not be included:
              </p>
              <ul className="max-h-20 overflow-y-auto">
                {unavailableSelected.map((file) => (
                  <li key={file.id}>{file.fileName}</li>
                ))}
              </ul>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                disabled={downloads.isBusy}
                onClick={() =>
                  setSelection(new Map(selected.map((file) => [file.id, file])))
                }
              >
                Clear unavailable selection
              </Button>
            </div>
          )}
          {hasSelection && (
            <Button
              type="button"
              className="w-full gap-2 rounded-xl"
              disabled={
                downloads.isBusy || exportFiles.length === 0 || exceedsLimit
              }
              onClick={() => void downloads.downloadFiles(exportFiles)}
            >
              <Download className="size-4" aria-hidden="true" />
              {`Download selected (${selected.length})`}
            </Button>
          )}
          {hasSelection && (
            <p
              className={cn(
                "text-xs leading-relaxed",
                exceedsLimit ? "text-amber-800" : "text-slate-500",
              )}
            >
              {exceedsLimit && "Select fewer files. "}ZIP downloads include up
              to {downloads.limits.maxFiles} files, totaling{" "}
              {vaultFileSize(downloads.limits.maxTotalBytes)}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
